import { z } from 'zod';

const datesSchema = z.union([
  z.object({ dueDate: z.string(), completionDate: z.string() }),
  z.object({ dueDate: z.string(), completionDate: z.null() }),
  z.object({ dueDate: z.null(), completionDate: z.string() }),
]);

export const getUserTransactionsResponseSchema = z.array(
  z
    .object({
      id: z.string(),
      type: z.enum(['PAYBACK', 'DEPOSIT', 'WITHDRAW', 'REFUND', 'SETUP_FEE', 'ID_PROTECT']),
      status: z.enum([
        'COMPLETE',
        'INCOMPLETE',
        'CHARGE',
        'IN_EXTENDED_PAYMENT_PERIOD',
        'OVERDUE',
        'PAYMENT_FREE_MONTH',
      ]),
      amount: z.number(),
      items: z.array(
        z.object({
          product: z.enum([
            'CREDIT',
            'DEBT_CONSOLIDATION',
            'PAYMENT_PROTECTION_INSURANCE',
            'ID_PROTECT',
            'LOAN',
            'LOAN_ID_PROTECT',
          ]),
          // The total amount of the transaction item
          amount: z.number(),
          details: z.array(
            z.discriminatedUnion('type', [
              z.object({
                type: z.enum([
                  'PRINCIPAL',
                  'INTEREST',
                  'OVERDUE_INTEREST',
                  'WITHDRAWAL_FEE',
                  'OVERDUE_FEE',
                  'DEBT_COLLECTION_FEE',
                  'DIRECT_DEBIT_MISSING_FEE',
                  'REMIT',
                  'UNKNOWN',
                  'TERMINATION',
                  'CAMPAIGN_DISCOUNT',
                  'COMPOUNDED_PRINCIPAL',
                  'CAPITALISED_COST',
                  'SETUP_FEE',
                  'INSTALLMENT_FEE',
                ]),
                amount: z.number(),
              }),
              z.object({
                type: z.literal('VAT'),
                amount: z.number(),
                rate: z.number(),
                durationInMonths: z.number().optional(),
              }),
              z.object({
                type: z.literal('PREMIUM'),
                amount: z.number(),
                durationInMonths: z.number().optional(),
              }),
            ]),
          ),
        }),
      ),
    })
    .and(datesSchema),
);

const idProtectActiveSchema = z.object({
  status: z.literal('ACTIVE'),
  validThrough: z.iso.datetime(),
  nextRepaymentAmount: z.number(),
  nextRepaymentDate: z.iso.datetime().optional(), // optional if the user has prepaid the product (eg. idprotect is bundled with a product)
  isThirdPartyActive: z.boolean(),
});

export const getUserIdProtectResponseSchema = z.discriminatedUnion('status', [
  idProtectActiveSchema,
  z.object({
    status: z.literal('CANCELED'),
    validThrough: z.iso.datetime(),
    isThirdPartyActive: z.boolean(),
  }),
  z.object({
    status: z.enum(['INACTIVE', 'TERMINATED']),
  }),
]);

export const getUserPaymentProtectionInsuranceResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.enum(['ACTIVE', 'CANCELED']),
    validThrough: z.iso.datetime(),
    nextRepaymentAmount: z.number().nullable(), // null if the user has 0 balance
    nextRepaymentDate: z.iso.datetime().nullable(), // null if the user has 0 balance
  }),
  z.object({
    status: z.enum(['INACTIVE', 'TERMINATED']),
  }),
]);

export const getUserTerminateAccountDetailsResponseSchema = z.object({
  credit: z.object({ debt: z.number() }).optional(),
  debtConsolidation: z.object({ debt: z.number() }).optional(),
  loan: z
    .union([
      z.object({ productVariant: z.literal('LOAN'), debt: z.number() }),
      z.object({
        productVariant: z.literal('LOAN_ID_PROTECT'),
        debt: z.number(),
        isInGracePeriod: z.literal(true),
        refundedAmount: z.number().optional(), // in case of overpayment
      }),
      z.object({
        productVariant: z.literal('LOAN_ID_PROTECT'),
        debt: z.number(),
        isInGracePeriod: z.literal(false),
        idProtectValidThrough: z.iso.datetime(),
      }),
    ])
    .optional(),
});

export const getCampaignResponseSchema = z.discriminatedUnion('isEligible', [
  z.object({
    isEligible: z.literal(false),
  }),
  z.object({
    isEligible: z.literal(true),
    isViewed: z.boolean(),
    costPerMonth: z.number(),
  }),
]);

export const activateCampaignResponseSchema = idProtectActiveSchema;

export const emailVerificationResponseSchema = z.object({
  resendAfter: z.iso.datetime(),
});

export const mobileVerificationResponseSchema = z.object({
  resendAfter: z.iso.datetime(),
});
